/**
 * Cliente API para Avexrh Worker
 * Conecta el cotizador con el backend de Cloudflare
 *
 * v2.1 - Error handling con timeouts y retry logic
 * v2.0 - Soporte para carrito híbrido
 */

/**
 * Wrapper para fetch() con timeout automático
 * Previene hangs infinitos cuando hay problemas de red
 * @param {string} url - URL a consultar
 * @param {Object} options - Opciones de fetch
 * @param {number} timeout - Tiempo máximo en milisegundos (default: 15s)
 * @returns {Promise<Response>}
 */
function fetchWithTimeout(url, options = {}, timeout = 15000) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return Promise.reject(new Error('Sin conexión a internet. Verifica tu conexión e intenta de nuevo.'));
  }

  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout: La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.')), timeout)
    )
  ]);
}

const AvexrhAPI = {
  // URL del Worker - Se puede sobrescribir desde Liquid con window.AVEXRH_API_URL
  baseUrl: null,

  /**
   * Obtener la URL base de la API
   * Prioridad: window.AVEXRH_API_URL > default
   */
  getBaseUrl() {
    if (this.baseUrl) return this.baseUrl;

    // Intentar obtener de configuración global
    if (typeof window !== 'undefined' && window.AVEXRH_API_URL) {
      this.baseUrl = window.AVEXRH_API_URL;
    } else {
      // Default
      this.baseUrl = 'https://api.imaneshernandez.com';
    }

    return this.baseUrl;
  },

  /**
   * Generar nombre único para archivo
   * Formato: {timestamp}_{randomId}_{nombreOriginal}
   */
  generateUniqueFileName(originalName) {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    // Limpiar nombre original de caracteres especiales
    const cleanName = originalName.replace(/[^a-zA-Z0-9._-]/g, '_');
    return `${timestamp}_${randomId}_${cleanName}`;
  },

  /**
   * Crear Draft Order en Shopify (flujo directo - un solo item)
   * @param {Object} params - Parámetros del pedido
   * @param {string} params.producto - ID del producto (vinyl, holografico, economico, iman, sheets, sheets_economicas)
   * @param {number} params.ancho - Ancho en cm
   * @param {number} params.alto - Alto en cm
   * @param {number} params.cantidad - Cantidad de piezas
   * @param {Array} params.disenos - Array de diseños [{nombre, url}]
   * @param {string} params.indicacionesEspeciales - Notas adicionales
   * @param {string} params.email - Email del cliente (opcional)
   * @param {boolean} params.primeraCompra - Si es primera compra (10% descuento)
   * @param {string} params.sessionId - ID de sesión para agrupar archivos
   * @returns {Promise<Object>} Respuesta con invoiceUrl para checkout
   */
  async createDraftOrder(params) {
    const {
      producto,
      ancho,
      alto,
      cantidad,
      disenos = [],
      indicacionesEspeciales = '',
      email = '',
      primeraCompra = false,
      sessionId = null,
    } = params;

    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/draft-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          producto,
          ancho,
          alto,
          cantidad,
          disenos,
          sessionId,
        }],
        indicacionesEspeciales,
        email,
        primeraCompra,
      }),
    }, 20000); // 20s timeout - proceso largo de creación de draft order

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.');
      } else if (response.status === 400) {
        throw new Error(data.error?.message || 'Datos inválidos. Verifica las dimensiones y cantidad.');
      } else if (response.status >= 500) {
        throw new Error('Problema en el servidor. Por favor intenta de nuevo en unos momentos.');
      }
      throw new Error(data.error?.message || 'Error al crear el pedido');
    }

    return data;
  },

  /**
   * Crear Draft Order Multi-Item (carrito híbrido)
   * @param {Object} params - Parámetros del pedido
   * @param {Array} params.items - Array de items [{producto, ancho, alto, cantidad, disenos, sessionId}]
   * @param {string} params.indicacionesEspeciales - Notas generales del pedido
   * @param {string} params.email - Email del cliente
   * @param {boolean} params.primeraCompra - Si aplica descuento primera compra
   * @returns {Promise<Object>} Respuesta con invoiceUrl para checkout
   */
  async createDraftOrderMulti(params) {
    const {
      items = [],
      indicacionesEspeciales = '',
      email = '',
      primeraCompra = false,
      customerId = null,
    } = params;

    if (items.length === 0) {
      throw new Error('Se requiere al menos un item en el pedido');
    }

    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/draft-order-multi`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
        indicacionesEspeciales,
        email,
        primeraCompra,
        customerId,
      }),
    }, 20000); // 20s timeout - proceso largo con múltiples items

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.');
      } else if (response.status === 400) {
        throw new Error(data.error?.message || 'Datos inválidos. Verifica los productos y cantidades.');
      } else if (response.status >= 500) {
        throw new Error('Problema en el servidor. Por favor intenta de nuevo en unos momentos.');
      }
      throw new Error(data.error?.message || 'Error al crear el pedido');
    }

    return data;
  },

  /**
   * Obtener cotización de precio
   * @param {string} producto - ID del producto
   * @param {number} ancho - Ancho en cm
   * @param {number} alto - Alto en cm
   * @param {number} cantidad - Cantidad de piezas
   * @returns {Promise<Object>} Datos de la cotización
   */
  async getQuote(producto, ancho, alto, cantidad) {
    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ producto, ancho, alto, cantidad }),
    }, 10000); // 10s timeout - cálculo rápido

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.');
      } else if (response.status === 400) {
        throw new Error(data.error?.message || 'Datos inválidos para cotización. Verifica dimensiones y cantidad.');
      } else if (response.status >= 500) {
        throw new Error('Problema al calcular el precio. Por favor intenta de nuevo.');
      }
      throw new Error(data.error?.message || 'Error al obtener cotización');
    }

    return data;
  },

  /**
   * Subir diseño a R2
   * @param {File} file - Archivo a subir
   * @param {string} sessionId - ID de sesión para agrupar archivos
   * @param {function} onProgress - Callback de progreso (opcional)
   * @returns {Promise<Object>} Datos del archivo subido
   */
  async uploadDesign(file, sessionId = null, onProgress = null) {
    const formData = new FormData();

    // Generar nombre único para evitar colisiones
    const uniqueFileName = this.generateUniqueFileName(file.name);

    // Crear nuevo File con nombre único
    const renamedFile = new File([file], uniqueFileName, { type: file.type });
    formData.append('file', renamedFile);

    // Agregar sessionId para organizar archivos por sesión
    if (sessionId) {
      formData.append('sessionId', sessionId);
    }

    // Usar XMLHttpRequest para reportar progreso si se proporciona callback
    if (onProgress && typeof onProgress === 'function') {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Configurar timeout en el XMLHttpRequest
        xhr.timeout = 30000; // 30s timeout para uploads

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.success) {
                resolve(data);
              } else {
                reject(new Error(data.error?.message || 'Error al subir el diseño'));
              }
            } catch (e) {
              reject(new Error('Error al procesar respuesta del servidor'));
            }
          } else {
            reject(new Error(`Error HTTP: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Error de red al subir el archivo'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Subida cancelada'));
        });

        xhr.addEventListener('timeout', () => {
          reject(new Error('Timeout: La subida tardó demasiado. Verifica tu conexión e intenta de nuevo.'));
        });

        xhr.open('POST', `${this.getBaseUrl()}/api/upload`);
        xhr.send(formData);
      });
    }

    // Usar fetch si no se necesita progreso
    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/upload`, {
      method: 'POST',
      body: formData,
    }, 30000); // 30s timeout para archivos grandes

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La subida tardó demasiado. Intenta con un archivo más pequeño o verifica tu conexión.');
      } else if (response.status === 400) {
        throw new Error(data.error?.message || 'Archivo inválido. Verifica el formato (PNG, JPG, PDF, AI).');
      } else if (response.status === 413) {
        throw new Error('Archivo muy grande. El tamaño máximo es 50MB.');
      } else if (response.status >= 500) {
        throw new Error('Problema al guardar el archivo. Por favor intenta de nuevo.');
      }
      throw new Error(data.error?.message || 'Error al subir el diseño');
    }

    return data;
  },

  /**
   * Eliminar sesión de diseños (rollback)
   * Usado cuando falla el proceso después de subir diseños
   * @param {string} sessionId - ID de sesión a eliminar
   * @returns {Promise<Object>} Resultado de la eliminación
   */
  async deleteSession(sessionId) {
    if (!sessionId) {
      throw new Error('SessionId requerido para eliminar');
    }

    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/designs/${sessionId}`, {
      method: 'DELETE',
    }, 10000); // 10s timeout para eliminación

    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La eliminación tardó demasiado. Verifica tu conexión.');
      } else if (response.status === 404) {
        // Sesión no encontrada - no es crítico, puede haber expirado
        return { success: true, message: 'Sesión ya eliminada' };
      } else if (response.status >= 500) {
        throw new Error('Problema al eliminar archivos. No afecta tu pedido.');
      }
      throw new Error(data.error?.message || 'Error al eliminar la sesión');
    }

    return data;
  },

  /**
   * Obtener configuración del sistema
   * @returns {Promise<Object>} Configuración de productos, límites, márgenes
   */
  async getConfig() {
    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/config`, {}, 8000); // 8s timeout
    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La solicitud tardó demasiado. Verifica tu conexión.');
      } else if (response.status >= 500) {
        throw new Error('No se pudo obtener la configuración. Recarga la página.');
      }
      throw new Error(data.error?.message || 'Error al obtener configuración');
    }

    return data;
  },

  /**
   * Health check de la API
   * @returns {Promise<Object>} Estado de la API
   */
  async healthCheck() {
    const response = await fetchWithTimeout(`${this.getBaseUrl()}/api/health`, {}, 5000); // 5s timeout
    const data = await response.json();

    if (!response.ok || !data.success) {
      // Mensajes específicos por código HTTP
      if (response.status === 408 || response.status === 504) {
        throw new Error('La API no responde. Verifica tu conexión.');
      } else if (response.status >= 500) {
        throw new Error('API temporalmente no disponible. Intenta de nuevo en unos momentos.');
      }
      throw new Error(data.error?.message || 'API no disponible');
    }

    return data;
  },
};

// Exportar globalmente
if (typeof window !== 'undefined') {
  window.AvexrhAPI = AvexrhAPI;
}
